// AI answer feedback and review.
// rateAnswer: thumbs up/down by the asker on their own rag_query_log row
// (rate_rag_answer RPC, addressed by the id runRAGQuery returned).

import { supabase } from '../lib/supabase';
import { logEvent } from '../lib/activityLogger';

/**
 * Records a thumbs up (true) / down (false) on one AI answer.
 * Also fires a rag_feedback activity event (not awaited).
 * @returns {Promise<boolean>} true when the rating was stored on the row.
 */
export const rateAnswer = async ({ logId, rating, query = '', answerLength = 0 } = {}) => {
  logEvent('rag_feedback', {
    log_id: logId || null,
    query: String(query || '').substring(0, 200),
    rating,
    answer_length: answerLength,
  });
  if (!logId) return false;
  const { data, error } = await supabase.rpc('rate_rag_answer', { p_log_id: logId, p_rating: rating });
  if (error) throw error;
  return data === true;
};

// ─── Needs-review queue (admin only) ─────────────────────────────────────────

export const REVIEW_REASONS = [
  { key: 'thumbs_down', label: 'Thumbs down', icon: 'thumb_down' },
  { key: 'no_answer', label: 'No answer', icon: 'help' },
  { key: 'partial', label: 'Partial answer', icon: 'rule' },
];

const QUEUE_MAX = 100; // RPC caps p_limit at 101 (page + 1 look-ahead row)

/**
 * One page of AI answers needing review, newest first.
 * @returns {Promise<{rows: Array, nextCursor: string|null}>}
 */
export const getReviewQueue = async ({
  hoaId, status = 'open', reasons = REVIEW_REASONS.map((r) => r.key),
  includeExcluded = false, includeArchived = false, before = null, limit = 25,
} = {}) => {
  if (!hoaId || !reasons?.length) return { rows: [], nextCursor: null };
  const pageSize = Math.min(Math.max(Number(limit) || 25, 1), QUEUE_MAX);
  const { data, error } = await supabase.rpc('admin_rag_review_queue', {
    p_hoa_id: hoaId,
    p_status: status,
    p_reasons: reasons,
    p_include_excluded: Boolean(includeExcluded),
    p_include_archived: Boolean(includeArchived),
    p_before: before || null,
    p_limit: pageSize + 1,
  });
  if (error) throw error;
  const all = data || [];
  const rows = all.slice(0, pageSize);
  const nextCursor = all.length > pageSize ? rows[rows.length - 1]?.asked_at || null : null;
  return { rows, nextCursor };
};

/** Open rows for the tab badge (capped: returns 100 when there are 100 or more). */
export const countOpen = async ({ hoaId, includeExcluded = false, includeArchived = false } = {}) => {
  const { rows } = await getReviewQueue({ hoaId, includeExcluded, includeArchived, limit: QUEUE_MAX });
  return rows.length;
};

const setReviewed = async ({ hoaId, logId, note = '', reopen = false }) => {
  const { data, error } = await supabase.rpc('admin_rag_mark_reviewed', {
    p_hoa_id: hoaId,
    p_log_id: logId,
    p_note: note || null,
    p_reopen: reopen,
  });
  if (error) throw error;
  return data === true;
};

export const markReviewed = ({ hoaId, logId, note }) => setReviewed({ hoaId, logId, note, reopen: false });

export const reopen = ({ hoaId, logId, note }) => setReviewed({ hoaId, logId, note, reopen: true });

export const ragReviewService = { rateAnswer, getReviewQueue, countOpen, markReviewed, reopen };
